import React from 'react'

import { CepTrophyIcon, Tooltip } from '@octane/spark'
import GraphiteInformationIcon from 'components/GraphiteInformationIcon'
import useAutoBonusTarget from 'hooks/useAutoBonusTarget'

import { AutoBonusTargetContainer } from './AutoBonusTarget.styled'

function AutoBonusTarget() {
    const {
        currentMonth,
        progressPercentage,
        remainingDealsToGoal,
        isBonusAchieved,
        remainingDaysInMonth,
        bonusProgram,
    } = useAutoBonusTarget()

    if (!bonusProgram) {
        return null
    }

    const isGoalOnly = bonusProgram.bonusPerDeal === 0
    const remainingDealWord = remainingDealsToGoal == 1 ? 'deal' : 'deals'
    const monthlyDealWord = bonusProgram?.monthlyGoal == 1 ? 'deal' : 'deals'

    const fundedDealWord =
        bonusProgram?.monthlyDealCount == 1 ? 'deal' : 'deals'

    return (
        <AutoBonusTargetContainer data-testid="auto-deals-bonus-container">
            <h3>
                {isGoalOnly ? 'Monthly Volume Goal' : 'Volume Bonus Target'}
            </h3>
            <div className="content" data-testid={`deals-target-bar-container`}>
                <header>
                    <span className="header-text">
                        <Tooltip size="large" showOnHover>
                            <Tooltip.Trigger>
                                <GraphiteInformationIcon data-testid="info-icon" />
                            </Tooltip.Trigger>
                            <Tooltip.Content>
                                <Tooltip.Header title="Monthly Deal Target" />
                                {isGoalOnly && (
                                    <>
                                        <p>
                                            Track your rooftop&apos;s progress
                                            toward this month&apos;s funded-deal
                                            goal.
                                        </p>
                                        <p>
                                            Your rooftop&apos;s goal is{' '}
                                            {bonusProgram.monthlyGoal}{' '}
                                            {monthlyDealWord} in {currentMonth}.
                                        </p>
                                    </>
                                )}
                                {!isGoalOnly && (
                                    <>
                                        <p>
                                            Hit your rooftop&apos;s target to
                                            unlock a $
                                            {bonusProgram.bonusPerDeal} per deal
                                            bonus. The bonus is applied
                                            retroactively and there&apos;s no
                                            limit on earnings!
                                        </p>
                                        <p>
                                            For example, if your target is{' '}
                                            {bonusProgram.monthlyGoal}{' '}
                                            {monthlyDealWord}, you&apos;ll earn
                                            $
                                            {bonusProgram.bonusPerDeal *
                                                bonusProgram.monthlyGoal}{' '}
                                            once you hit{' '}
                                            {bonusProgram.monthlyGoal} and an
                                            additional $
                                            {bonusProgram.bonusPerDeal} for
                                            every extra deal.
                                        </p>
                                    </>
                                )}
                            </Tooltip.Content>
                        </Tooltip>

                        {isBonusAchieved && (
                            <>
                                {isGoalOnly && (
                                    <>
                                        Great job &mdash; your rooftop hit its{' '}
                                        {currentMonth} goal!
                                    </>
                                )}
                                {!isGoalOnly && (
                                    <>
                                        Great job - your rooftop is earning $
                                        {bonusProgram.bonusPerDeal} per deal!
                                    </>
                                )}
                            </>
                        )}
                        {!isBonusAchieved && (
                            <>
                                {isGoalOnly && (
                                    <>
                                        Fund {bonusProgram.monthlyGoal}{' '}
                                        {monthlyDealWord} in {currentMonth} to
                                        hit your rooftop&apos;s goal
                                    </>
                                )}
                                {!isGoalOnly && (
                                    <>
                                        Fund {bonusProgram.monthlyGoal}{' '}
                                        {monthlyDealWord} in {currentMonth} to
                                        unlock a ${bonusProgram.bonusPerDeal}
                                        /deal bonus
                                    </>
                                )}
                            </>
                        )}
                    </span>
                    <span>
                        {remainingDaysInMonth} day
                        {remainingDaysInMonth === 1 ? '' : 's'} remaining
                    </span>
                </header>
                <div className="container">
                    <div className="bar">
                        {progressPercentage > 0 && (
                            <div
                                className="progress"
                                style={{ width: `${progressPercentage}%` }}
                            />
                        )}
                    </div>
                </div>
                <footer>
                    {isBonusAchieved && (
                        <>
                            <div>
                                <span className="footer-text text-success">
                                    <CepTrophyIcon />{' '}
                                    {isGoalOnly ? 'Goal Hit!' : 'Target Hit!'}
                                </span>
                                <small>*Dashboard is refreshed hourly.</small>
                            </div>
                            <span className="footer-info">
                                {isGoalOnly && (
                                    <>
                                        {currentMonth} goal reached &bull; Keep
                                        funding deals
                                    </>
                                )}
                                {!isGoalOnly && (
                                    <>
                                        Bonus applied to all {currentMonth}{' '}
                                        deals &bull; No limit on earnings
                                    </>
                                )}
                            </span>
                        </>
                    )}
                    {!isBonusAchieved && (
                        <>
                            <div>
                                <span className="footer-text">
                                    {currentMonth} Progress:{' '}
                                    {bonusProgram.monthlyDealCount}{' '}
                                    {fundedDealWord} funded
                                </span>
                                <small>*Dashboard is refreshed hourly.</small>
                            </div>
                            <span className="text-success">
                                {remainingDealsToGoal} {remainingDealWord} to{' '}
                                go!
                            </span>
                        </>
                    )}
                </footer>
            </div>
        </AutoBonusTargetContainer>
    )
}

export default AutoBonusTarget
